import { cn } from "@/lib/utils";
import { AlertCircle, AlertTriangle, CheckCircle, Info, X } from "lucide-react";
import * as React from "react";
import { Button } from "./button";

interface BannerProps {
  variant?: "info" | "success" | "warning" | "critical" | "danger";
  title?: string;
  description?: string;
  onClose?: () => void;
  showClose?: boolean;
  showButtonAction?: boolean;
  buttonActionTitle?: string;
  className?: string;
  children?: React.ReactNode;
}

const Banner = React.forwardRef<HTMLDivElement, BannerProps>(
  (
    {
      variant = "info",
      title,
      description,
      onClose,
      showClose = true,
      showButtonAction,
      buttonActionTitle,
      className,
      children,
    },
    ref
  ) => {
    const bannerClasses = cn("wl-banner", `wl-banner-${variant}`, className);

    const iconMap = {
      info: Info,
      success: CheckCircle,
      warning: AlertTriangle,
      critical: AlertCircle,
      danger: AlertCircle,
    };

    const Icon = iconMap[variant];

    return (
      <div ref={ref} className={bannerClasses}>
        <div className="wl-banner-icon">
          <Icon size={24} />
        </div>

        <div className="wl-banner-title">
          {title && <div className="font-medium">{title}</div>}
          {description && (
            <div className="text-sm opacity-90">{description}</div>
          )}
          {children}
        </div>

        {showClose && onClose && (
          <>
            <div className="wl-d-flex wl-align-items-center wl-gap-8 wl-ms-auto">
              {showButtonAction && (
                <Button variant="white" onClick={onClose}>
                  {buttonActionTitle || "Text"}
                </Button>
              )}
              <div className="wl-banner-btn-wrap">
                <button
                  type="button"
                  className="wl-banner-close"
                  onClick={onClose}
                  aria-label="Close banner"
                >
                  <X size={24} className="wl-cursor-pointer" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }
);

Banner.displayName = "Banner";

// Multi-line Banner component
interface BannerMultiProps {
  variant?: "warning" | "critical" | "danger";
  title?: string;
  subtitle?: string;
  onClose?: () => void;
  buttonActionTitle?: string;
  showClose?: boolean;
  className?: string;
  children?: React.ReactNode;
}

const BannerMulti = React.forwardRef<HTMLDivElement, BannerMultiProps>(
  (
    {
      variant = "warning",
      title,
      subtitle,
      onClose,
      buttonActionTitle = "Text",
      showClose = true,
      className,
      children,
    },
    ref
  ) => {
    const bannerClasses = cn(
      "wl-banner-multi",
      `wl-banner-multi-${variant}`,
      className
    );

    const iconMap = {
      warning: AlertTriangle,
      critical: AlertCircle,
      danger: AlertCircle,
    };

    const Icon = iconMap[variant];

    return (
      <div ref={ref} className={bannerClasses}>
        <div className="wl-banner-multi-icon">
          <Icon size={24} />
        </div>

        <div className="wl-banner-multi-title-wrap">
          {title && <div className="wl-banner-multi-title">{title}</div>}
          {subtitle && (
            <div className="wl-banner-multi-sub-title">{subtitle}</div>
          )}
          {children}
        </div>

        {showClose && onClose && (
          <div className="wl-d-flex wl-align-items-center wl-gap-16 wl-ms-auto">
            <button
              onClick={onClose}
              type="button"
              aria-label="Close banner"
              className="wl-btn wl-btn-primary-outline"
            >
              {buttonActionTitle}
            </button>
            <X
              size={24}
              fill="currentColor"
              onClick={onClose}
              className="wl-cursor-pointer"
            />
          </div>
        )}
      </div>
    );
  }
);

BannerMulti.displayName = "BannerMulti";

export { Banner, BannerMulti };
